<script setup>
import { Link } from '@inertiajs/vue3';
import { computed, onUnmounted, ref, watch } from 'vue';

const props = defineProps({
    fund: { type: Array, default: () => [] },
    /** Millisekunder mellem skift. Kommer fra config/tilbud.php. */
    interval: { type: Number, default: 6000 },
});

const plads = ref(0);
let ur = null;

const aktuel = computed(() => props.fund[plads.value] ?? null);

function naeste() {
    if (props.fund.length > 1) {
        plads.value = (plads.value + 1) % props.fund.length;
    }
}

function start() {
    stop();

    /*
     * Ingen rotation af ét element. Et ur der taeller til én er stoej i
     * eventloopet og et blink for ingenting.
     */
    if (props.fund.length > 1) {
        ur = setInterval(naeste, props.interval);
    }
}

function stop() {
    if (ur) {
        clearInterval(ur);
        ur = null;
    }
}

/*
 * HOLDER PLADSEN INDEN FOR LISTEN.
 *
 * Samme fejl som produktkarrusellen havde i dag: bliver listen kortere, mens
 * pladsen peger paa den gamle laengde, vises der INGENTING. Her ville det
 * vaere en tom annonceplads — altsaa praecis det vi er ved at fjerne.
 */
watch(() => props.fund.length, (antal) => {
    if (plads.value > antal - 1) {
        plads.value = Math.max(0, antal - 1);
    }

    start();
}, { immediate: true });

onUnmounted(stop);

function kroner(v) {
    const n = Number(v);

    return Number.isFinite(n)
        ? new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(n)
        : '';
}
</script>

<template>
    <div v-if="fund.length" class="fund">
        <!--
            MAERKNINGEN SKAL VAERE TYDELIG.
            Markedsfoeringslovens krav om reklameidentifikation gaelder ogsaa
            her: laeseren skal kunne se at det er en annonce UDEN at lede
            efter det. Derfor et maerkat med ramme oeverst, ikke en graa note
            nederst — og en linje der siger hvad vi tjener paa det.
        -->
        <div class="fund__maerkat">
            <span class="fund__maerkat-tekst">Annonce</span>
        </div>

        <h4 class="fund__titel">Prisen er faldet</h4>

        <!-- Én ad gangen. Rotationen holder pause mens musen er over, saa
             varen ikke skifter i det oejeblik man vil klikke paa den. -->
        <div class="fund__rude" @mouseenter="stop" @mouseleave="start">
            <Transition name="fund-skift" mode="out-in">
                <Link
                    v-if="aktuel"
                    :key="aktuel.id"
                    :href="`/produkt/${aktuel.slug}`"
                    class="fund__vare"
                    rel="sponsored"
                >
                    <img
                        v-if="aktuel.billede_id"
                        :src="`/images/${aktuel.billede_id}?w=160`"
                        :alt="aktuel.navn"
                        class="fund__billede"
                        loading="lazy"
                    />
                    <div class="fund__tekst">
                        <span class="fund__navn">{{ aktuel.navn }}</span>
                        <span class="fund__priser">
                            <span class="fund__foer">{{ kroner(aktuel.pris_foer) }}</span>
                            <span class="fund__nu">{{ kroner(aktuel.pris_nu) }}</span>
                        </span>
                        <span class="fund__fald">&minus;{{ aktuel.fald_procent }} % på 7 dage</span>
                    </div>
                </Link>
            </Transition>
        </div>

        <div v-if="fund.length > 1" class="fund__prikker">
            <button
                v-for="(vare, i) in fund"
                :key="vare.id"
                type="button"
                class="fund__prik"
                :class="{ 'fund__prik--aktiv': i === plads }"
                :aria-label="`Vis tilbud ${i + 1} af ${fund.length}`"
                @click="plads = i; start()"
            ></button>
        </div>

        <p class="fund__note">
            Faldet er målt af os på varens laveste pris inkl. fragt, ikke på butikkens egen førpris.
            Vi kan tjene provision, hvis du køber via et link.
        </p>
    </div>
</template>

<style scoped>
.fund {
    border: 1px solid var(--pc-border);
    border-radius: 0.5rem;
    padding: 0.9rem;
}

/* Maerkatet skal ses foerst, ikke sidst. */
.fund__maerkat {
    margin-bottom: 0.6rem;
}

.fund__maerkat-tekst {
    display: inline-block;
    border: 1px solid var(--pc-text-muted);
    border-radius: 0.25rem;
    padding: 0.05rem 0.45rem;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--pc-text-secondary);
}

.fund__titel {
    font-size: 0.95rem;
    font-weight: 700;
    margin: 0 0 0.6rem;
}

/* Fast hoejde, saa sidebaren ikke hopper naar varen skifter. */
.fund__rude {
    min-height: 74px;
}

.fund__vare {
    display: flex;
    gap: 0.7rem;
    align-items: center;
    padding: 0.5rem 0;
    text-decoration: none;
    color: inherit;
}

.fund__vare:hover .fund__navn {
    color: var(--pc-accent);
}

.fund__billede {
    width: 56px;
    height: 56px;
    object-fit: contain;
    flex: 0 0 56px;
}

.fund__tekst {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
}

.fund__navn {
    font-size: 0.82rem;
    line-height: 1.25;
    /* To linjer: ellers bestemmer det laengste produktnavn hoejden. */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.fund__priser {
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
}

.fund__foer {
    font-size: 0.75rem;
    color: var(--pc-text-muted);
    text-decoration: line-through;
}

.fund__nu {
    font-weight: 700;
    color: var(--pc-accent);
}

.fund__fald {
    font-size: 0.72rem;
    color: var(--pc-success);
    font-weight: 600;
}

.fund__prikker {
    display: flex;
    gap: 0.3rem;
    justify-content: center;
    margin-top: 0.5rem;
}

.fund__prik {
    width: 7px;
    height: 7px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: var(--pc-border);
    cursor: pointer;
}

.fund__prik--aktiv {
    background: var(--pc-accent);
}

.fund-skift-enter-active,
.fund-skift-leave-active {
    transition: opacity 0.35s ease;
}

.fund-skift-enter-from,
.fund-skift-leave-to {
    opacity: 0;
}

/* Respekterer systemets valg om mindre bevaegelse. */
@media (prefers-reduced-motion: reduce) {
    .fund-skift-enter-active,
    .fund-skift-leave-active {
        transition: none;
    }
}

.fund__note {
    margin: 0.7rem 0 0;
    font-size: 0.68rem;
    line-height: 1.35;
    color: var(--pc-text-muted);
}
</style>
